import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // Using Link for client-side routing
import { List, X } from 'react-bootstrap-icons'; // Icons
import logo from '../assets/navbar-logo.webp';
import logoBlack from '../assets/tms-logo-black.webp';
import fgcLogo from '../assets/fgc-logo-white.webp';
import fgcLogoBlack from '../assets/fgc-logo-black.webp';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    // Define routes that should have light navbar with different backgrounds
    const whiteNavbarRoutes = ['/about-us']; // #FFFFFF background
    const grayNavbarRoutes = ['/how-it-works', '/security']; // #F1F1F1 background
    const isLightNavbar = whiteNavbarRoutes.includes(location.pathname) || grayNavbarRoutes.includes(location.pathname);
    const isGrayNavbar = grayNavbarRoutes.includes(location.pathname);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const navLinks = [
        { name: "Home", path: "/" },
        { name: "Features", path: "/features" },
        { name: "How it Works", path: "/how-it-works" },
        { name: "Security", path: "/security" },
        { name: "About Us", path: "/about-us" },
    ];

    // Conditional classes based on navbar theme
    const navBgClass = isGrayNavbar ? 'bg-[#F1F1F1] border-gray-200' : (isLightNavbar ? 'bg-white border-gray-200' : 'bg-[#0B1221] border-gray-800');
    const textClass = isLightNavbar ? 'text-gray-700 hover:text-black' : 'text-gray-300 hover:text-white';
    const buttonBgClass = isLightNavbar ? 'bg-[#1A1A1A] hover:bg-[#090E1B]' : 'bg-[#FFFFFF] hover:bg-gray-100';
    const buttonTextClass = isLightNavbar ? 'text-white' : 'text-black';
    const mobileMenuBgClass = isLightNavbar ? 'bg-white' : 'bg-[#0B1221]';
    const mobileLinkBorderClass = isLightNavbar ? 'border-gray-200' : 'border-gray-800';
    const hamburgerBgClass = isLightNavbar ? 'bg-gray-100 text-gray-700 hover:text-black hover:bg-gray-200' : 'bg-gray-900 text-gray-400 hover:text-white hover:bg-gray-800';

    // Conditional logos
    const currentLogo = isLightNavbar ? logoBlack : logo;
    const currentFgcLogo = isLightNavbar ? fgcLogo : fgcLogoBlack;

    return (
        <nav className={`${navBgClass} w-full z-50 fixed top-0 left-0 transition-colors duration-300`}>
            <div className="site-shell">
                <div className="flex items-center justify-between h-20">
                    {/* Logo Section */}
                    <div className="flex-shrink-0 flex items-center cursor-pointer">
                        <Link to="/">
                            <img className="h-9 w-auto" src={currentLogo} alt="TMS Logo" />
                        </Link>
                    </div>

                    {/* Desktop Menu - Changed to lg breakpoint */}
                    <div className="hidden lg:block">
                        <div className="flex items-baseline space-x-2 xl:space-x-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`${textClass} px-2 xl:px-3 py-2 rounded-[16px] text-[13px] xl:text-sm transition-all duration-200 whitespace-nowrap ${location.pathname === link.path
                                        ? `font-extrabold ${isLightNavbar ? 'text-black' : 'text-white'}`
                                        : 'font-medium'
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* CTA Buttons (Desktop) - Changed to lg breakpoint */}
                    <div className="hidden lg:flex items-center gap-3">
                        <Link
                            to="/schedule-demo"
                            className="bg-[#00C2FF] hover:bg-[#00a0d6] text-white font-bold px-4 xl:px-6 py-2 rounded-[12px] border border-transparent transition-all duration-300 shadow-sm whitespace-nowrap text-xs xl:text-sm"
                        >
                            Request a demo
                        </Link>
                        <Link
                            to="https://fintechglobal.center/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 ${buttonBgClass} ${buttonTextClass} px-3 xl:px-4 py-2 rounded-[12px] transition-all duration-300 font-medium whitespace-nowrap text-xs xl:text-sm shadow-sm`}>
                            <span>Explore</span>
                            <img src={currentFgcLogo} alt="FGC Logo" className="h-4 w-auto" />
                        </Link>
                    </div>

                    {/* Mobile Menu Button - Changed to lg breakpoint */}
                    <div className="-mr-2 flex lg:hidden">
                        <button
                            onClick={toggleMenu}
                            type="button"
                            className={`${hamburgerBgClass} inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white transition-colors duration-300`}
                            aria-controls="mobile-menu"
                            aria-expanded="false"
                        >
                            <span className="sr-only">Open main menu</span>
                            {isOpen ? (
                                <X className="block h-8 w-8" aria-hidden="true" />
                            ) : (
                                <List className="block h-8 w-8" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu (Slide down/Expand) - Changed to lg breakpoint */}
            <div className={`lg:hidden ${isOpen ? 'block' : 'hidden'} transition-all duration-300 ease-in-out ${mobileMenuBgClass}`} id="mobile-menu">
                <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`${textClass} block px-3 py-4 rounded-md text-base border-b ${mobileLinkBorderClass} ${location.pathname === link.path
                                ? `font-extrabold ${isLightNavbar ? 'text-black bg-gray-100 border-l-4 border-l-black' : 'text-white bg-gray-800/50 border-l-4 border-l-[#00C2FF]'}`
                                : 'font-medium'
                                }`}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <div className="mt-10 px-3 pb-6 flex flex-col gap-3">
                        <Link
                            to="/schedule-demo"
                            onClick={() => setIsOpen(false)}
                            className="w-full flex justify-center items-center bg-[#00C2FF] hover:bg-[#00a0d6] text-white px-6 py-3 rounded-[16px] transition-all duration-300 font-bold shadow-sm"
                        >
                            Request a demo
                        </Link>
                        <Link
                            to="https://fintechglobal.center/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`w-full flex justify-center items-center gap-3 ${buttonBgClass} ${buttonTextClass} px-6 py-3 rounded-[16px] transition-all duration-300 font-bold shadow-sm`}>
                            <span>Explore</span>
                            <img src={currentFgcLogo} alt="FGC Logo" className="h-5 w-auto" />
                        </Link>
                    </div>
                </div>
            </div>
        </nav >
    );
};

export default Navbar;
